import { z } from "zod"

export const loginRequestSchema = z.object({
	usernameOrEmail: z.string(),
	password: z.string(),
})

export type LoginRequestSchema = z.infer<typeof loginRequestSchema>

export const authSuccessSchema = z.object({
	username: z.string(),
	isAdmin: z.boolean(),
	error: z.undefined(),
})

export type AuthSuccessSchema = z.infer<typeof authSuccessSchema>

export const authErrorMessages = ["emailNotVerified", "invalidCredentials", "notLoggedIn"] as const

export const authErrorSchema = z.object({
	error: z.enum(authErrorMessages),
})

export type AuthErrorSchema = z.infer<typeof authErrorSchema>

export const loginResponseSchema = z.discriminatedUnion("error", [authErrorSchema, authSuccessSchema])

export type LoginResponseSchema = z.infer<typeof loginResponseSchema>

// needed custom mail validation since the one from zod is pretty shitty (does not accept subdomains)
// https://emailregex.com/
// last visited 2023-11-30
const emailRegex =
	/^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/

export const validateEmail = (mail: string) => emailRegex.test(mail)

export const REGISTRATION_CONSTRAINTS = {
	USERNAME_MIN_LENGTH: 3,
	USERNAME_MAX_LENGTH: 20,
	USERNAME_REGEX: /^[a-zA-Z0-9_]*$/,
	PASSWORD_MIN_LENGTH: 8,
	PASSWORD_MAX_LENGTH: 255,
	DISPLAY_NAME_MIN_LENGTH: 3,
	DISPLAY_NAME_MAX_LENGTH: 255,
	EMAIL_MAX_LENGTH: 255,
	EMAIL_REGEX: emailRegex,
}

const R = REGISTRATION_CONSTRAINTS

const noWhitespace = (str: string) => !/\s/.test(str)

export const registerRequestSchema = z
	.object({
		username: z.string().min(R.USERNAME_MIN_LENGTH).max(R.USERNAME_MAX_LENGTH).refine(noWhitespace),
		email: z.string().max(R.EMAIL_MAX_LENGTH).refine(validateEmail),
		displayName: z.string().min(R.DISPLAY_NAME_MIN_LENGTH).max(R.DISPLAY_NAME_MAX_LENGTH).optional(),
		password: z.string().min(R.PASSWORD_MIN_LENGTH, { message: "passwordTooShort" }).max(R.PASSWORD_MAX_LENGTH),
		repeatPassword: z.string(),
		hcaptchaToken: z.string().default(""),
	})
	.strict()
export type RegisterRequestSchema = z.infer<typeof registerRequestSchema>

export type RegistrationErrors =
	| "passwordTooCommon"
	| "passwordMismatch"
	| "userOrEmailAlreadyExists"
	| "emailNotSent"
	| "currentlyDisabled"

export const verifyEmailRequestSchema = z.object({
	token: z.string().refine((t) => t.match(/:/g)?.length == 3),
})

export type VerifyEmailRequestSchema = z.infer<typeof verifyEmailRequestSchema>

export type VerificationErrors = "tokenTimeout" | "invalidToken"

export const getUsernameAndEmailRequestSchema = z.object({
	usernameOrEmail: z.string(),
})
export type GetEmailAndUsernameRequestSchema = z.infer<typeof getUsernameAndEmailRequestSchema>

export type ResetPasswordErrors = "emailNotSent" | "userNotFound" | "tokenTimeout" | "invalidToken" | "passwordMismatch"

export const resetPasswordRequestSchema = z.object({
	password: z.string(),
	passwordRepeat: z.string(),
	token: z.string().refine((t) => t.match(/:/g)?.length == 3),
})
export type ResetPasswordRequestSchema = z.infer<typeof resetPasswordRequestSchema>
